import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Radar, Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/exceptionHub";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div className={`transition-all duration-500 ${scrolled ? "backdrop-blur-xl bg-white/80 border-b border-slate-100 shadow-soft" : "bg-transparent border-b border-transparent"}`}>
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-navy text-white transition-transform group-hover:rotate-12">
              <Radar className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </span>
            <div className="leading-none">
              <span className="text-[15px] font-bold text-navy font-heading tracking-tight">Exception Hub™</span>
              <span className="block text-[10px] text-ink-400 tracking-wide mt-0.5">DeMaxtore Workspace</span>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="px-3.5 py-2 text-sm font-medium text-ink-500 hover:text-navy rounded-full hover:bg-slate-50 transition-colors">
                {l.label}
              </a>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a href="#final-cta" data-testid="nav-signin" className="text-sm font-medium text-ink-500 hover:text-navy transition-colors">Sign in</a>
            <a href="#final-cta" data-testid="nav-cta" className="group inline-flex items-center gap-1.5 h-10 pl-5 pr-4 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy-soft transition-colors">
              Create Workspace
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
            </a>
          </div>

          <button className="md:hidden grid place-items-center h-10 w-10 rounded-xl border border-slate-200 text-navy" onClick={() => setOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-100">
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="py-2.5 text-[15px] font-medium text-ink-700">{l.label}</a>
              ))}
              <a href="#final-cta" onClick={() => setOpen(false)} className="mt-2 inline-flex items-center justify-center h-11 rounded-full bg-navy text-white text-sm font-medium">Create Your Free Workspace</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
